import React from 'react';

interface StatCard {
  value: string;
  label: string;
  sub: string;
}

interface ExperienceItem {
  role: string;
  company: string;
  period: string;
  current?: boolean;
  highlights: string[];
}

const STATS: StatCard[] = [
  { value: '9.5+', label: 'Years Experience', sub: 'Full-Stack Development' },
  { value: '75+', label: 'Projects Shipped', sub: 'Web & Cloud' },
  { value: '3', label: 'Roles Held', sub: 'Jr → Sr Full Stack Engineer (Architect)' },
  { value: '3', label: 'Awards', sub: 'Star Performer + Webimation + Problem Solving Certifications' },
];

const EXPERIENCE: ExperienceItem[] = [
  {
    role: 'Sr. Full Stack Architect',
    company: 'Rayvat Outsourcing (Jenya)',
    period: '2017 – Present',
    current: true,
    highlights: [
      'Architected MERN-stack SaaS platforms serving thousands of users',
      'Led migration from monolith to microservices (Docker + AWS)',
      'Mentored junior developers and conducted code reviews',
      'Implemented CI/CD pipelines reducing deployment time by 60%',
    ],
  },
  {
    role: 'Full Stack Web Developer',
    company: 'SYNC Technologies Pvt. Ltd.',
    period: '2016 – 2017',
    highlights: [
      'Developed PHP/CodeIgniter/Wordpress end to end web apps from scratch',
      'Integrated third-party payment gateways and REST APIs',
    ],
  },
  {
    role: 'Jr. Web Developer',
    company: 'RJITech IT Solution Pvt. Ltd.',
    period: '2015 – 2016',
    highlights: [
       'Developed PHP/CodeIgniter/Wordpress end to end web apps from scratch',
      'Delivered responsive UI with Bootstrap',
    ],
  },
];

const Stats: React.FC = () => {
  return (
    <section id="about" className="py-28 bg-[#0a0a0f]">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="mb-16">
          <p className="text-emerald-400 font-mono text-sm tracking-widest uppercase mb-3">
            Background
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-white">Experience</h2>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
          {STATS.map((s) => (
            <div
              key={s.label}
              className="p-6 rounded-xl bg-[#13131e] border border-slate-800 hover:border-emerald-500/30 transition-all duration-200 group"
            >
              <div className="text-4xl font-black text-emerald-400 group-hover:text-emerald-300 transition-colors leading-none mb-2">
                {s.value}
              </div>
              <div className="text-sm font-bold text-white mb-0.5">{s.label}</div>
              <div className="text-xs text-slate-500">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Experience timeline */}
        <div className="relative space-y-0">
          {/* Vertical line */}
          <div className="absolute left-0 md:left-[11px] top-0 bottom-0 w-px bg-slate-800" />

          {EXPERIENCE.map((exp, i) => (
            <div key={i} className="relative pl-8 md:pl-10 pb-12 last:pb-0">
              {/* Dot */}
              <div
                className={`absolute left-[-5px] md:left-[5px] top-1.5 w-4 h-4 rounded-full border-2 ${
                  exp.current
                    ? 'bg-emerald-400 border-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.6)]'
                    : 'bg-slate-800 border-slate-600'
                }`}
              />

              <div className="p-6 rounded-xl bg-[#13131e] border border-slate-800 hover:border-slate-700 transition-all duration-200">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-white">{exp.role}</h3>
                    <p className="text-emerald-400 font-semibold text-sm">{exp.company}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {exp.current && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono">
                        Current
                      </span>
                    )}
                    <span className="text-slate-500 text-sm font-mono">{exp.period}</span>
                  </div>
                </div>
                <ul className="space-y-1.5">
                  {exp.highlights.map((h, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-400">
                      <span className="text-emerald-500 mt-0.5 shrink-0">›</span>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Education strip */}
        <div className="mt-16 grid md:grid-cols-2 gap-4">
          {[
            {
              degree: 'MCA – Master of Computer Application',
              institute: 'Kadi University, Gandhinagar',
              year: '2015',
              score: '7.22 CPI',
            },
            {
              degree: 'BCA – Bachelor of Computer Application',
              institute: 'HNGU University, Kadi, Mehsana',
              year: '2012',
              score: '60%',
            },
          ].map((edu) => (
            <div
              key={edu.degree}
              className="p-5 rounded-xl bg-[#13131e] border border-slate-800 flex items-start gap-4"
            >
              <div className="text-2xl mt-0.5">🎓</div>
              <div>
                <div className="font-bold text-white text-sm">{edu.degree}</div>
                <div className="text-slate-400 text-sm">{edu.institute}</div>
                <div className="text-slate-500 text-xs mt-1 font-mono">
                  {edu.year} · {edu.score}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;
